/**
 * element-props.ts
 *
 * Reads a UI-element node's field from wherever the v4 schema permits it to
 * live: the element's own top level, OR nested one level under `props`
 * (`ui.cogo.schema.json` `$defs.Element.props`: "Props object passed to
 * island/block-like components", `additionalProperties: true`). Generation
 * output routinely nests a configured field under `props` even when the
 * schema ALSO declares that same field at the element's top level (e.g.
 * `columns`, `text`, `variant`, `bind`) — confirmed against real
 * `fwd2-expense-approval` bundles (see the validator-props-fix report below).
 *
 * This is the validator-side counterpart of cogo-react-renderer's
 * `liftV4Props` (`element-renderer.tsx`) — the SAME schema fact, the SAME
 * discovery order (top level wins; `props` fills in only when the top-level
 * field is absent), independently re-implemented here because
 * `cogo-validator` must not depend on `cogo-react-renderer` (separate
 * packages, no such dependency exists or should be added).
 *
 * Any validator rule that checks whether an Element node HAS a field, or
 * reads a field's VALUE for a pass/fail decision, MUST go through
 * `getElementField`/`hasElementField` — reading `node[field]` alone
 * reproduces the exact blindness this module exists to fix. Concretely: the
 * `element-required-prop` rule read `node.columns` directly and reported a
 * false-positive "missing required prop 'columns'" on every `data-table`
 * whose columns were correctly authored at `node.props.columns` — see
 * `.superpowers/sdd/2026-08-07-specdet-spec-driven-determinism/
 * validator-props-fix-report.md`.
 *
 * Mirrors the precedent set by `utils/event-bindings.ts` for the analogous
 * "one physical fact, multiple accepted shapes" problem in `events.json`.
 */

/**
 * Read `field` from `node`, checking the top level first and falling back to
 * `node.props[field]` when the top-level value is absent. Top-level always
 * wins when both are present (matches `liftV4Props`'s discovery order).
 */
export function getElementField(
  node: Record<string, unknown> | null | undefined,
  field: string,
): unknown {
  if (!node || typeof node !== "object") return undefined
  const topLevel = node[field]
  if (topLevel !== undefined) return topLevel
  const props = node.props
  if (props && typeof props === "object" && !Array.isArray(props)) {
    return (props as Record<string, unknown>)[field]
  }
  return undefined
}

/** True when `field` is present (non-`undefined`) at either location. */
export function hasElementField(
  node: Record<string, unknown> | null | undefined,
  field: string,
): boolean {
  return getElementField(node, field) !== undefined
}

/**
 * Write `value` to wherever `field` currently lives on `node` — `props` if
 * the field is already nested there (and not also present at the top
 * level), the top level otherwise (including when the field is absent from
 * both, matching prior top-level-only write behavior). For fixers that
 * rewrite an existing field's content in place without changing its
 * location.
 */
export function setElementField(
  node: Record<string, unknown>,
  field: string,
  value: unknown,
): void {
  if (node[field] === undefined) {
    const props = node.props
    if (
      props &&
      typeof props === "object" &&
      !Array.isArray(props) &&
      (props as Record<string, unknown>)[field] !== undefined
    ) {
      ;(props as Record<string, unknown>)[field] = value
      return
    }
  }
  node[field] = value
}
